import { CdkMenuModule } from "@angular/cdk/menu";
import { CommonModule } from "@angular/common";
import {
    Component,
    computed,
    effect,
    HostBinding,
    input,
    OnDestroy,
    signal,
} from "@angular/core";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { auditTime, Subject } from "rxjs";
import { uniqBy } from "../../../../helpers/uniq-by";
import { Menu } from "../../../menu/menu.component";
import { MenuOption } from "../../../menu/option/menu-option.component";
import { CommonEventAdapter } from "../../adapters/event.adapter";
import {
    TIMELINE_CALENDAR_DEFAULT_TOP,
    TimelineSlot,
} from "../../calendar.types";
import { TimelineCalendarEventContent } from "./content/event-content.component";

dayjs.extend(isBetween);

@Component({
    selector: "app-timeline-event",
    templateUrl: "./event.component.html",
    styleUrls: ["./event.component.scss"],
    standalone: true,
    imports: [
        CdkMenuModule,
        Menu,
        MenuOption,
        TimelineCalendarEventContent,
        CommonModule,
    ],
})
export class TimelineEvent extends CommonEventAdapter implements OnDestroy {
    @HostBinding("class") class = "absolute z-10 px-2";
    @HostBinding("style.top") get getTop() {
        return `${TIMELINE_CALENDAR_DEFAULT_TOP}px`;
    }
    @HostBinding("style.left") get getLeft() {
        return `${this.left()}px`;
    }
    @HostBinding("style.height") get getHeight() {
        if (!this.height()) return "auto";

        return `${this.height()}px`;
    }
    @HostBinding("style.width") get getWidth() {
        if (!this.width()) return "auto";

        return `${this.width()}px`;
    }

    private destroy$ = new Subject<void>();

    left = signal(0);
    width = signal(0);
    height = signal(0);
    time = input.required<string>();
    day = input.required<Dayjs>();
    slots = input.required<TimelineSlot[]>();
    order = signal(0);

    isCanceled = computed(
        () => this.event()?.metadata?.enrollmentSchedule?.status === "CANCELED",
    );

    constructor() {
        super();

        effect(() => {
            this.calculate();
            this.store.mode$.subscribe({
                next: () => {
                    this.calculate();
                },
            });
            this.store.resize$.pipe(auditTime(300)).subscribe({
                next: () => {
                    this.calculate();
                },
            });
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    calculate() {
        this.handleHeight();

        const event = this.event();
        if (!event) return;

        const startDate = dayjs(event.startDate);
        const endDate = dayjs(event.endDate);

        const startDayNumeric = startDate.format("DD");
        const dayElement = document.querySelector(
            `.day[data-day='${startDayNumeric}']`,
        );
        const dayWidth = dayElement?.clientWidth ?? 0;

        const hourSpan = Math.round(
            Number(endDate.format("HH")) - Number(startDate.format("HH")) + 1,
        );

        const sameDayEvents = this.slots()
            .filter((slot) => slot.day === startDayNumeric)
            .flatMap((slot) => slot.events.filter((ev) => !ev.allDay));

        const uniqEvents = uniqBy(sameDayEvents, (ev) => ev.id);

        const overlappingEvents = uniqEvents.filter((ev) => {
            const evStart = dayjs(ev.startDate);
            const evEnd = dayjs(ev.endDate);

            if (evStart.format("HH") === startDate.format("HH")) return true;

            return !(evEnd.isBefore(startDate) || evStart.isAfter(endDate));
        });

        let overlap = 1;
        let order = 0;

        const currentSlots = this.slots().filter(
            (slot) => slot.day === startDayNumeric,
        );

        currentSlots.forEach((slot) => {
            for (let i = 0; i <= hourSpan; i++) {
                const date = startDate.add(i, "hour");
                if (slot.time === date.format("HH:00")) {
                    if (overlappingEvents.length > 1) {
                        overlap = overlappingEvents.length;
                        order = overlappingEvents.findIndex(
                            (e) => e.id === event.id,
                        );
                    } else if (overlap >= sameDayEvents.length) {
                        overlap = sameDayEvents.length;
                        order = sameDayEvents.indexOf(event);
                    }
                }
            }
        });

        this.width.set(overlap > 1 ? dayWidth / overlap : dayWidth);

        const mode = this.store.mode();
        let leftPosition: number;
        if (mode === "week") {
            const weekStart = this.day().startOf("week");
            const dayIndex = startDate.startOf("day").diff(weekStart, "day");
            leftPosition = this.getWeekLeft(dayWidth, dayIndex);
        } else {
            const daysDifference = Number(startDayNumeric);
            leftPosition = this.getLeftPosition(dayWidth, daysDifference);
        }

        const orderLeft = order > 0 ? this.width() * order : 0;

        this.left.set(
            sameDayEvents.length > 1 ? leftPosition + orderLeft : leftPosition,
        );
    }

    private getLeftPosition(dayWidth: number, daysDifference: number): number {
        if (this.store.mode() === "day") return 130;
        if (this.store.mode() === "week") {
            // 주 모드에서는 getWeekLeft 사용 (여기서는 안전 fallback)
            return 130 + dayWidth * (daysDifference - 1);
        }
        const divider = 1 * (daysDifference - 2);
        return 130 + divider + (daysDifference - 1) * dayWidth;
    }

    private getWeekLeft(dayWidth: number, dayIndex: number): number {
        // 주 시작 offset (기존 130 유지)
        return 130 + dayIndex * dayWidth;
    }

    private handleHeight(): void {
        const event = this.event();
        const startDate = dayjs(event.startDate);
        const endDate = dayjs(event.endDate);

        const diff = Math.round(
            Number(endDate.format("HH")) - Number(startDate.format("HH")) + 1,
        );

        if (event)
            if (diff > 1) {
                // (블록 높이 x 시간차) - 패딩
                this.height.set(48 * diff - 16);
            }
    }
}
